/**
 * Løkkeværn og engangsflag om den automatiske Microsoft-viderestilling på `/login`
 * (frontend 0.21.1, project.md "Auth (fase 2, Entra)" – mønsteret fra optimize/livetracking).
 *
 * Er Microsoft-login slået til, sender LoginPage brugeren videre med det samme. Kommer
 * Microsoft tilbage uden fejlkode og uden at der blev sat en session (fx en afbrudt
 * consent-dialog), ville det uden et værn blive en uendelig løkke. Værnet er en tæller med
 * tidsstempel i `sessionStorage`, sat lige før hver viderestilling: kommer brugeren tilbage
 * inden for vinduet uden en session, viser siden sig selv i stedet for at forsøge igen.
 *
 * Al adgang til sessionStorage er i try/catch: i privat browsing e.l. virker værnet ikke,
 * men det blokerer heller aldrig, og intet crasher. Modulet har med vilje ingen imports.
 */

const GUARD_KEY = 'hbc-entra-redirect';
const LOGOUT_KEY = 'hbc-explicit-logout';
export const REDIRECT_GUARD_WINDOW_MS = 60_000;

interface RedirectGuardRecord {
  n: number;
  t: number;
}

function readGuard(): RedirectGuardRecord | null {
  try {
    const raw = sessionStorage.getItem(GUARD_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (typeof record?.n === 'number' && typeof record?.t === 'number') return record;
  } catch {
    /* privat browsing e.l.: værnet virker da ikke, men blokerer heller ikke */
  }
  return null;
}

/**
 * Har et forsøg været sat i gang inden for det seneste minut? Læses SYNKRONT under render,
 * før LoginPage beslutter noget om en viderestilling – aldrig i en effekt, der når at køre
 * først.
 */
export function isRedirectGuardBlocked(): boolean {
  const record = readGuard();
  return !!record && record.n >= 1 && Date.now() - record.t < REDIRECT_GUARD_WINDOW_MS;
}

/** Sættes lige før `window.location.assign(...)` til Microsoft – automatisk eller ved klik. */
export function markRedirectAttempt(): void {
  try {
    sessionStorage.setItem(GUARD_KEY, JSON.stringify({ n: 1, t: Date.now() }));
  } catch {
    /* ignore */
  }
}

/**
 * Ryddes, når der er et token (AuthContext), og når en `login_error` vises (fejlen er en
 * bedre forklaring end værnet, og et senere besøg skal kunne forsøge automatikken forfra).
 */
export function clearRedirectGuard(): void {
  try {
    sessionStorage.removeItem(GUARD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Engangsflag: "brugeren klikkede selv Log ud". Adskilt fra løkkeværnet med vilje – værnet
 * betyder "automatikken prøvede og fik ingen session" (en fejltilstand), et bevidst log ud
 * er ikke en fejl. Sat af `logout()`; læst og ryddet af LoginPage. Uden det ville
 * automatikken sende brugeren stille ind igen via Microsofts egen SSO-session. Et
 * session-udløb (global 401-handler) sætter IKKE flaget, så der viderestilles stille igen.
 */
export function markExplicitLogout(): void {
  try {
    sessionStorage.setItem(LOGOUT_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function hasExplicitLogout(): boolean {
  try {
    return sessionStorage.getItem(LOGOUT_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearExplicitLogout(): void {
  try {
    sessionStorage.removeItem(LOGOUT_KEY);
  } catch {
    /* ignore */
  }
}
